import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, PlusCircle, User, Menu, LogOut, Sparkles, Wallet, Wind, BookOpen, Shield, TriangleAlert, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getAvatarUrl, initials } from "@/lib/helpers";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-2xl transition-colors duration-200 ${
          isActive ? "text-[#E8621A]" : "text-[#1B2A4A]/60 hover:text-[#1B2A4A]"
        }`
      }
    >
      <Icon className="w-5 h-5" strokeWidth={2.25} />
      <span className="text-[11px] font-medium">{label}</span>
    </NavLink>
  );
}

function SideNavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-2 rounded-xl transition-colors duration-200 ${
          isActive
            ? "bg-[#E8621A] text-white"
            : "text-[#1B2A4A]/80 hover:bg-[#1B2A4A]/5 hover:text-[#1B2A4A]"
        }`
      }
    >
      <Icon className="w-[18px] h-[18px]" strokeWidth={2.25} />
      <span className="text-sm font-semibold">{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isRelax = location.pathname.startsWith("/relaxation");

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 h-screen border-r border-[#1B2A4A]/10 bg-white/60 backdrop-blur-xl fixed inset-y-0 left-0 z-30 overflow-hidden">
        <div className="px-6 pt-4 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-[#1B2A4A] text-lg leading-tight">Surviving Together</h1>
              <p className="text-xs text-[#1B2A4A]/60">Thriving Together</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 space-y-0.5">
          <SideNavItem to="/" icon={Home} label="Community Feed" testId="nav-home" />
          <SideNavItem to="/post" icon={PlusCircle} label="Share a Tip" testId="nav-post" />
          <SideNavItem to="/members" icon={Users} label="Members" testId="nav-members" />
          <div className="pt-1.5 pb-0.5 px-4 text-[10px] uppercase tracking-widest text-[#1B2A4A]/40 font-semibold">Toolbox</div>
          <SideNavItem to="/budget" icon={Wallet} label="Simple Budget" testId="nav-budget" />
          <SideNavItem to="/relaxation" icon={Wind} label="Relaxation Room" testId="nav-relaxation" />
          <div className="pt-1.5 pb-0.5 px-4 text-[10px] uppercase tracking-widest text-[#1B2A4A]/40 font-semibold">Account</div>
          <SideNavItem to="/profile" icon={User} label="Profile" testId="nav-profile" />
          <SideNavItem to="/membership" icon={Sparkles} label="Membership" testId="nav-membership" />
          <SideNavItem to="/community-guidelines" icon={BookOpen} label="Community Guidelines" testId="nav-guidelines" />
          {user?.role === "admin" && (
            <SideNavItem to="/admin" icon={Shield} label="Admin" testId="nav-admin" />
          )}
        </nav>

        <div className="shrink-0 px-3 pt-2 pb-2 border-t border-[#1B2A4A]/8">
          <div className="flex items-center gap-3 p-2 rounded-xl bg-[#F7F5F2]">
            <Avatar className="w-9 h-9">
              <AvatarImage src={getAvatarUrl(user?.avatar_path)} />
              <AvatarFallback className="bg-[#2A9D8F] text-white text-xs font-bold">
                {initials(user?.first_name, user?.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1B2A4A] truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-[#1B2A4A]/60 truncate">{user?.zip_code}</p>
            </div>
            <Button
              data-testid="btn-logout-desktop"
              variant="ghost"
              size="icon"
              onClick={doLogout}
              className="h-8 w-8 text-[#1B2A4A]/60 hover:text-[#E76F51]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-center text-[#1B2A4A]/40 mt-1.5">Powered by SocialImpactCLE</p>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 glass-nav border-b">
        <div className="flex items-center justify-between px-5 py-3">
          <NavLink to="/" className="flex items-center gap-2" data-testid="mobile-brand">
            <div className="w-9 h-9 rounded-xl bg-[#E8621A] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-[#1B2A4A] text-sm">Surviving Together</p>
              <p className="text-[10px] text-[#1B2A4A]/60">Thriving Together</p>
            </div>
          </NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="mobile-menu-trigger" variant="ghost" size="icon" className="rounded-full">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={getAvatarUrl(user?.avatar_path)} />
                  <AvatarFallback className="bg-[#2A9D8F] text-white text-xs font-bold">
                    {initials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl w-56">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold text-[#1B2A4A]">{user?.first_name} {user?.last_name}</div>
                <div className="text-xs text-[#1B2A4A]/60 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
                <User className="w-4 h-4 mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-members" onClick={() => navigate("/members")}>
                <Users className="w-4 h-4 mr-2" /> Members
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-membership" onClick={() => navigate("/membership")}>
                <Sparkles className="w-4 h-4 mr-2" /> Membership
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-guidelines" onClick={() => navigate("/community-guidelines")}>
                <BookOpen className="w-4 h-4 mr-2" /> Community Guidelines
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-privacy" onClick={() => navigate("/privacy")}>
                <Shield className="w-4 h-4 mr-2" /> Privacy Policy
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-terms" onClick={() => navigate("/terms")}>
                <BookOpen className="w-4 h-4 mr-2" /> Terms of Service
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                  <Shield className="w-4 h-4 mr-2" /> Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="text-[#E76F51]">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 pb-24 lg:pb-0 min-h-screen">
        {user && user.warnings_count > 0 && !isRelax && (
          <div data-testid="warnings-banner" className="mx-4 sm:mx-6 lg:mx-10 mt-4 lg:mt-6 rounded-2xl bg-[#F4A261]/15 border border-[#F4A261]/40 px-4 py-3 flex items-start gap-3">
            <TriangleAlert className="w-5 h-5 text-[#B8752E] shrink-0 mt-0.5" />
            <div className="text-sm text-[#1B2A4A]">
              <p className="font-semibold">You have {user.warnings_count} of 3 warnings.</p>
              <p className="text-[#1B2A4A]/75 mt-0.5">
                Please review our{" "}
                <NavLink to="/community-guidelines" data-testid="warnings-guidelines-link" className="text-[#E8621A] font-semibold hover:underline">
                  Community Guidelines
                </NavLink>. Three warnings will pause your account.
              </p>
            </div>
          </div>
        )}
        <div className={isRelax ? "" : "max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10"}>
          {children}
        </div>
        <footer className="hidden lg:flex flex-col items-center gap-1 text-center text-xs text-[#1B2A4A]/40 pb-6">
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <NavLink to="/community-guidelines" data-testid="footer-guidelines" className="text-[#1B2A4A]/60 hover:text-[#E8621A] transition-colors duration-200 font-semibold">
              Community Guidelines
            </NavLink>
            <span className="text-[#1B2A4A]/30">·</span>
            <NavLink to="/privacy" data-testid="footer-privacy" className="text-[#1B2A4A]/60 hover:text-[#E8621A] transition-colors duration-200 font-semibold">
              Privacy Policy
            </NavLink>
            <span className="text-[#1B2A4A]/30">·</span>
            <NavLink to="/terms" data-testid="footer-terms" className="text-[#1B2A4A]/60 hover:text-[#E8621A] transition-colors duration-200 font-semibold">
              Terms of Service
            </NavLink>
          </div>
          <span>Powered by SocialImpactCLE</span>
        </footer>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass-nav border-t">
        <div className="grid grid-cols-5 gap-1 px-2 py-2">
          <NavItem to="/" icon={Home} label="Feed" testId="bottom-nav-home" />
          <NavItem to="/post" icon={PlusCircle} label="Post" testId="bottom-nav-post" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="bottom-nav-toolbox"
                className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-2xl text-[#1B2A4A]/60 hover:text-[#1B2A4A] transition-colors duration-200 ${
                  (location.pathname === "/budget" || location.pathname === "/relaxation") ? "text-[#E8621A]" : ""
                }`}
              >
                <Menu className="w-5 h-5" strokeWidth={2.25} />
                <span className="text-[11px] font-medium">Toolbox</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="rounded-2xl w-56 mb-2">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-[#1B2A4A]/40">Toolbox</DropdownMenuLabel>
              <DropdownMenuItem data-testid="toolbox-budget" onClick={() => navigate("/budget")}>
                <Wallet className="w-4 h-4 mr-2 text-[#2A9D8F]" /> Simple Budget
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="toolbox-relaxation" onClick={() => navigate("/relaxation")}>
                <Wind className="w-4 h-4 mr-2 text-[#8E44AD]" /> Relaxation Room
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavItem to="/membership" icon={Sparkles} label="Tiers" testId="bottom-nav-membership" />
          <NavItem to="/profile" icon={User} label="Profile" testId="bottom-nav-profile" />
        </div>
        <p className="text-[9px] text-center text-[#1B2A4A]/40 pb-1">Powered by SocialImpactCLE</p>
      </nav>
    </div>
  );
}
